import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { sv } from "date-fns/locale";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
    useDeleteAllNotifications,
    useDeleteNotification,
    useMarkAllNotificationsRead,
    useMarkNotificationRead,
    useMarkNotificationsSeen,
    useNotificationInbox,
    useNotificationUnseenCount,
    useNotificationUnreadCount,
} from "@/queries/notifications";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { resolveShellRoute } from "@/lib/shellRoutes";

function NotificationInboxSkeleton() {
    return (
        <div className="native-v3-notification-list py-2" aria-label="Laddar notiser">
            <div className="border-t border-border/60">
                {Array.from({ length: 5 }).map((_, index) => (
                    <div className="border-b border-border/60 px-4 py-4" key={index}>
                        <div className="flex items-start gap-3">
                            <Skeleton className="h-9 w-9 shrink-0" />
                            <div className="min-w-0 flex-1 space-y-2">
                                <div className="flex items-center justify-between gap-4">
                                    <Skeleton className="h-4 w-2/5" />
                                    <Skeleton className="h-3 w-14" />
                                </div>
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-3/4" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NotificationRow({ notification, onOpen, onRemove, isRemoving }) {
    const isRead = notification.status === "read" || Boolean(notification.readAt);
    const isSeen = Boolean(notification.seenAt);
    const [showSeenDot, setShowSeenDot] = useState(!isSeen);
    const hasDeeplink = Boolean(notification.data?.deeplink || notification.data?.url || notification.deeplink);
    const createdAt = notification.createdAt
        ? typeof notification.createdAt === "string"
            ? parseISO(notification.createdAt)
            : new Date(notification.createdAt)
        : null;

    const ago = createdAt
        ? formatDistanceToNowStrict(createdAt, { addSuffix: true, locale: sv })
        : "";

    useEffect(() => {
        if (!isSeen) {
            setShowSeenDot(true);
            return undefined;
        }
        const timer = window.setTimeout(() => {
            setShowSeenDot(false);
        }, 750);
        return () => window.clearTimeout(timer);
    }, [isSeen, notification.id, notification._id]);

    return (
        <div
            className={`native-v3-notification-row flex min-h-[118px] items-start gap-3 px-4 py-3 transition ${
                isRead
                    ? "text-muted-foreground active:bg-muted/45 [@media(hover:hover)]:hover:bg-muted/60"
                    : "bg-muted/40 active:bg-muted/60 [@media(hover:hover)]:hover:bg-muted/60"
            }`}
            data-read={isRead}
        >
            <button
                type="button"
                onClick={onOpen}
                className="native-v3-notification-open min-w-0 flex min-h-[94px] flex-1 flex-col justify-between text-left"
                disabled={isRemoving}
            >
                <div>
                    <div className="flex items-start justify-between gap-2">
                        <div
                            className={`native-v3-notification-title min-w-0 break-all font-medium text-sm leading-tight ${isRead ? "text-muted-foreground" : "text-foreground"}`}
                            style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                        >
                            {notification.title || "Notis"}
                        </div>
                        <span
                            aria-hidden="true"
                            className={`native-v3-notification-dot mt-1 h-2 w-2 rounded-full bg-red-500 transition-all duration-300 ease-out ${
                                showSeenDot ? "scale-100 opacity-100" : "scale-0 opacity-0"
                            }`}
                        />
                    </div>
                    {notification.body ? (
                        <div className="mt-1 min-h-[42px]">
                            <div
                                className="native-v3-notification-body min-w-0 whitespace-pre-wrap break-all text-sm leading-snug text-muted-foreground"
                                style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                            >
                                {notification.body}
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-[42px]" />
                    )}
                </div>
                <div className="mt-2 flex min-h-8 items-start justify-between gap-3">
                    {ago ? <div className="native-v3-notification-time pt-1 text-xs text-muted-foreground">{ago}</div> : <span />}
                    <span className="native-v3-notification-open-label inline-flex min-h-8 items-center gap-1 text-xs font-medium">
                        {hasDeeplink ? "Öppna" : "Läs meddelande"}
                        <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                </div>
            </button>
            <Button
                variant="ghost"
                size="icon"
                className="native-v3-notification-remove mt-0.5 h-8 w-8 shrink-0 self-start items-start text-muted-foreground hover:text-destructive"
                onClick={onRemove}
                disabled={isRemoving}
            >
                {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span className="sr-only">Ta bort</span>
            </Button>
        </div>
    );
}

export default function NotificationBell({ triggerClassName = "", iconClassName = "h-5 w-5" }) {
    const [open, setOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(8);
    const [confirmClearAllOpen, setConfirmClearAllOpen] = useState(false);
    const [pendingDeleteNote, setPendingDeleteNote] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const seenRequestedRef = useRef(new Set());
    const navigate = useNavigate();
    const { requestOfficialShell } = useProtocolShell();

    const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useNotificationInbox({
        enabled: open,
    });
    const { data: unreadCount = 0 } = useNotificationUnreadCount();
    const { data: unseenCount = 0 } = useNotificationUnseenCount();
    const markOne = useMarkNotificationRead();
    const markSeen = useMarkNotificationsSeen();
    const markAll = useMarkAllNotificationsRead();
    const deleteOne = useDeleteNotification();
    const deleteAll = useDeleteAllNotifications();

    const inboxPages = data?.pages || [];
    const inbox = inboxPages.flatMap((page) => page?.items || []);
    const hasNotifications = inbox.length > 0;
    const visibleNotifications = inbox.slice(0, visibleCount);
    const canShowMore = inbox.length > visibleCount || Boolean(hasNextPage);
    const showMoreHasUnseen = canShowMore && unseenCount > 0;

    const handleOpenItem = (note) => {
        if (!note) return;
        const id = note.id || note._id;
        if (id && note.status !== "read") {
            markOne.mutate(id);
        }

        const deeplink = note.data?.deeplink || note.data?.url || note.deeplink;
        if (deeplink) {
            const shellRoute = resolveShellRoute(deeplink);
            if (shellRoute?.type === "official_match") {
                requestOfficialShell(shellRoute.officialId);
                setOpen(false);
                return;
            }
            navigate(deeplink);
            setOpen(false);
            return;
        }

        setSelectedMessage(note);
    };

    const handleShowMore = async () => {
        const nextVisibleCount = visibleCount + 8;
        if (nextVisibleCount <= inbox.length) {
            setVisibleCount(nextVisibleCount);
            return;
        }
        if (hasNextPage && !isFetchingNextPage) {
            await fetchNextPage();
            setVisibleCount(nextVisibleCount);
            return;
        }
        setVisibleCount(Math.min(nextVisibleCount, inbox.length));
    };

    useEffect(() => {
        if (open) setVisibleCount(8);
        if (!open) {
            seenRequestedRef.current = new Set();
        }
    }, [open]);

    useEffect(() => {
        if (!open || !visibleNotifications.length || markSeen.isPending) return undefined;
        const idsToMarkSeen = visibleNotifications
            .filter((note) => !note?.seenAt)
            .map((note) => String(note.id || note._id || ""))
            .filter((id) => id && !seenRequestedRef.current.has(id));

        if (!idsToMarkSeen.length) return undefined;

        idsToMarkSeen.forEach((id) => seenRequestedRef.current.add(id));
        markSeen.mutate(idsToMarkSeen, {
            onError: () => {
                idsToMarkSeen.forEach((id) => seenRequestedRef.current.delete(id));
            },
        });
        return undefined;
    }, [open, visibleNotifications, markSeen]);

    return (
        <>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={`relative ${triggerClassName}`.trim()}
                        aria-label="Notiser"
                    >
                        {isFetching ? (
                            <Loader2 className={`${iconClassName} animate-spin`} />
                        ) : (
                            <Bell className={iconClassName} />
                        )}
                        {unseenCount > 0 ? (
                            <span className="absolute right-1 top-1 inline-flex h-2 w-2 rounded-full bg-red-500" />
                        ) : null}
                    </Button>
                </SheetTrigger>
                <SheetContent
                    side="right"
                    className="native-v3-notification-sheet flex h-full flex-col gap-0 overflow-hidden p-0 pt-[env(safe-area-inset-top)] sm:max-w-md"
                    style={{ width: "min(100vw, 420px)" }}
                >
                    <SheetHeader className="native-v3-notification-header border-b px-4 pb-3 pr-14 pt-4 text-left">
                        <SheetTitle className="text-base">Notiser</SheetTitle>
                        <SheetDescription className="text-xs text-muted-foreground">
                            Hantera dina uppdateringar och påminnelser.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="native-v3-notification-toolbar border-b px-4 py-2">
                        <div className="grid grid-cols-2 gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="native-v3-notification-action h-9 text-xs"
                                onClick={() => markAll.mutate()}
                                disabled={markAll.isPending || unreadCount === 0}
                            >
                                <CheckCheck className="mr-2 h-4 w-4" /> Markera alla lästa
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="native-v3-notification-action native-v3-notification-action-danger h-9 text-xs text-destructive hover:text-destructive"
                                onClick={() => setConfirmClearAllOpen(true)}
                                disabled={deleteAll.isPending || !hasNotifications}
                            >
                                {deleteAll.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Trash2 className="mr-2 h-4 w-4" />
                                )}
                                Rensa alla
                            </Button>
                        </div>
                    </div>
                    <div className="native-v3-notification-scroll flex-1 overflow-y-auto">
                        {isLoading ? (
                            <NotificationInboxSkeleton />
                        ) : hasNotifications ? (
                            <div className="native-v3-notification-list py-2">
                                <ul className="border-t border-border/60">
                                    {visibleNotifications.map((note, index) => {
                                        const id = note.id || note._id;
                                        const isRemoving = deletingId === id && deleteOne.isPending;
                                        return (
                                            <li key={id}>
                                                <NotificationRow
                                                    notification={note}
                                                    onOpen={() => handleOpenItem(note)}
                                                    onRemove={() => setPendingDeleteNote(note)}
                                                    isRemoving={isRemoving}
                                                />
                                            </li>
                                        );
                                    })}
                                </ul>
                                {canShowMore ? (
                                    <div className="pt-3 text-center">
                                        <Button
                                            className="native-v3-notification-more relative"
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleShowMore}
                                            disabled={isFetchingNextPage}
                                        >
                                            {isFetchingNextPage ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Laddar...
                                                </>
                                            ) : (
                                                <>
                                                    Visa fler
                                                    {showMoreHasUnseen ? (
                                                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                                                            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
                                                            {unseenCount} nya
                                                        </span>
                                                    ) : null}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="flex h-full items-center justify-center px-4 py-12 text-center text-sm text-muted-foreground">
                                Inget att se.
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmClearAllOpen} onOpenChange={setConfirmClearAllOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ta bort alla notiser?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Detta tar bort hela din notislista. Åtgärden går inte att ångra.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                deleteAll.mutate(undefined, {
                                    onSuccess: () => {
                                        setConfirmClearAllOpen(false);
                                        setVisibleCount(8);
                                    },
                                });
                            }}
                        >
                            Rensa alla
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={Boolean(pendingDeleteNote)}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen && !deleteOne.isPending) setPendingDeleteNote(null);
                }}
            >
                <AlertDialogContent className="native-v3-notification-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Ta bort notisen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Notisen tas bort permanent från din inkorg.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Avbryt</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteOne.isPending}
                            onClick={(event) => {
                                event.preventDefault();
                                const id = pendingDeleteNote?.id || pendingDeleteNote?._id;
                                if (!id) return;
                                setDeletingId(id);
                                deleteOne.mutate(id, {
                                    onSuccess: () => setPendingDeleteNote(null),
                                    onSettled: () => setDeletingId(null),
                                });
                            }}
                        >
                            {deleteOne.isPending ? "Tar bort..." : "Ta bort"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={Boolean(selectedMessage)} onOpenChange={(nextOpen) => !nextOpen && setSelectedMessage(null)}>
                <DialogContent className="native-v3-notification-message">
                    <DialogHeader className="native-v3-notification-message-header">
                        <DialogTitle>{selectedMessage?.title || "Notis"}</DialogTitle>
                        <DialogDescription>
                            {selectedMessage?.createdAt
                                ? new Date(selectedMessage.createdAt).toLocaleString("sv-SE", {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                  })
                                : "Meddelande från Lapstr"}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="native-v3-notification-message-body whitespace-pre-wrap">
                        {selectedMessage?.body || "Meddelandet saknar innehåll."}
                    </div>
                    <DialogFooter>
                        <Button type="button" onClick={() => setSelectedMessage(null)}>Stäng</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
